import { FieldInput } from './FieldInput';
import { FileField } from './FileField';
import type { RoomAnswer, RoomSection } from './types';

/**
 * The current section's fields — but only the ones the engine says are VISIBLE right now. Visibility
 * is recomputed by the wizard on every change and passed down, so a select that reveals a follow-up
 * field shows it the instant it is chosen, exactly as the server will evaluate it at completion.
 */
export function SectionView({
  section,
  visibleFieldIds,
  answers,
  onChange,
  onBlur,
  onUpload,
}: {
  section: RoomSection;
  visibleFieldIds: Set<string>;
  answers: Record<string, RoomAnswer>;
  onChange: (fieldId: string, value: string) => void;
  onBlur?: (fieldId: string) => void;
  /** Runs the 3-step file upload for a field; the progress callback drives the field's bar. */
  onUpload: (fieldId: string, file: File, onProgress: (pct: number) => void) => Promise<void>;
}) {
  const fields = section.fields.filter((f) => visibleFieldIds.has(f.id));

  return (
    <section className="room-card" aria-labelledby={`sec-${section.id}`}>
      <h1 id={`sec-${section.id}`} className="room-card-title">
        {section.title}
      </h1>

      <div className="room-form">
        {fields.map((field) => (
          // File fields span both columns — an upload control + progress bar needs the full width and
          // sits awkwardly paired next to a short text input.
          <div key={field.id} className={field.type === 'file' ? 'room-field room-field--wide' : 'room-field'}>
            <label className="room-field-label" htmlFor={`room-field-${field.id}`}>
              {field.label}
              {field.required && (
                <span className="room-req" aria-hidden="true">
                  {' '}
                  *
                </span>
              )}
            </label>
            {field.type === 'file' ? (
              <FileField
                field={field}
                file={answers[field.id]?.file ?? null}
                onUpload={(f, onProgress) => onUpload(field.id, f, onProgress)}
              />
            ) : (
              <FieldInput
                field={field}
                value={answers[field.id]?.value}
                onChange={(v) => onChange(field.id, v)}
                onBlur={onBlur ? () => onBlur(field.id) : undefined}
              />
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
